<template>
    <div id="map_tool" 
        class="map-tool"
    >
        <div class="map-tool__btn" 
            @click="is_open_setbar=!is_open_setbar"
        >
            <v-icon v-if="!is_hide_all" 
                color="white"
            >
                icon-more
            </v-icon>
            <v-icon v-else 
                color="white"
            >
                icon-visibility-off
            </v-icon>
        </div>

        <div v-show="is_open_setbar" 
            class="map-tool__setbar"
        >
            <v-btn
                class="map-tool__close-btn"
                icon
                @click="is_open_setbar = !is_open_setbar"
            >
                <v-icon color="white">
                    icon-close
                </v-icon>
            </v-btn>
            <div 
                class="map-tool__container"
            >
                <div v-if="legends.items.length" 
                    class="map-tool__title"
                >
                    <v-icon color="white">
                        icon-toolbar
                    </v-icon>
                    {{ legends.title }}
                </div>
                <div class="map-tool__content">
                    <label v-for="(item, item_key) in legends.items" 
                        :key="item_key"
                    >
                        <input
                            v-model="legend_options_checked"
                            :value="item.id"
                            :name="item.name"
                            :type="input_type"
                            @change="check_legend_option(item.type)"
                        >
                        <div 
                            :class="[
                                { checked: legend_options_checked.includes(item.id) }, 
                                input_type
                            ]"
                        />
                        <span>{{ item.name }}</span>
                    </label>
                </div>
                <div v-if="colorbars.items.length" 
                    class="map-tool__title"
                >
                    <v-icon color="white">
                        icon-palette
                    </v-icon>
                    {{ colorbars.title }}
                </div>
                <div class="map-tool__content">
                    <label v-for="(item, item_key) in colorbars.items" 
                        :key="item_key"
                    >
                        <input
                            v-model="colorbar_options_checked"
                            :value="item.id"
                            :name="item.name"
                            :type="input_type"
                            @change="check_color_bar_option()"
                        >
                        <div 
                            :class="[
                                { checked: colorbar_options_checked.includes(item.id) }, 
                                input_type
                            ]"
                        />
                        <span>{{ item.name }}</span>
                    </label>
                </div>
            </div>
            <div 
                class="map-tool__hide-all" 
                :class="{'disable': is_hide_all}"
                @click="is_hide_all = !is_hide_all"
            >
                <v-icon>icon-visibility-off</v-icon>
                <div>
                    {{ $t('__emap.hide_legend_colorbar') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'LegendColorbarSetting',

        props: {
            legends: {
                type: Object,
                default: () => ({})
            },
            
            colorbars: {
                type: Object,
                default: () => ({})
            },
            
            input_type: {
                type: String,
                require: true,
                default: '',
            },
            
            input_limit: {
                type: Number,
                require: true,
                default: -1,
            },

            legend_type_list: {
                type: Object,
                default: () => ({})
            },
        },

        data() {
            return {
                is_open_setbar: false,
                legend_options_checked: [],
                colorbar_options_checked: [],
                is_hide_all: false,
                legend_lock: {
                    lightning: false,
                    typhoon: false,
                    monitor: false,
                    gauge: false,
                    traffic_live: false,
                }
            }
        },

        computed: {
            all_options() {
                return [this.legends, this.colorbars]
            }
        },

        watch: {
            all_options: {
                handler() {
                    this.init_all_options_checked ();
                    this.transmit_options_status();
                },
                immediate: true
            },

            is_hide_all() {
                this.transmit_options_status();
            },

            legend_type_list: {
                handler() {
                    this.set_legend_lock()
                },
                immediate: true,
                deep: true
            }
        },

        methods: {
            init_all_options_checked () {
                const init_options_checked = (options) => {
                    return options
                        .filter(({ default_display }) => default_display)
                        .map(({ id }) => id);
                }
                this.colorbar_options_checked = init_options_checked(this.colorbars.items)
                this.legend_options_checked = init_options_checked(this.legends.items);
            },

            set_legend_lock() {
                this.legend_type_list
                const check_default_ckecked = (id) => this.legend_options_checked.includes(id)

                this.legend_lock = {
                    lightning: this.legend_type_list['lightning'].some(check_default_ckecked),
                    typhoon: this.legend_type_list['typhoon'].some(check_default_ckecked),
                    monitor: this.legend_type_list['monitor'].some(check_default_ckecked),
                    station: this.legend_type_list['station'].some(check_default_ckecked),
                    gauge: this.legend_type_list['gauge'].some(check_default_ckecked),
                    traffic_live: this.legend_type_list['traffic_live'].some(check_default_ckecked),
                }
            },

            check_legend_option(type) {
                if (this._is_options_exceed_limit(this.legend_options_checked)) {
                    this.legend_options_checked = this._unshift_first_option_checked(this.legend_options_checked)
                }
                if (this.input_limit === 1) {
                    this.colorbar_options_checked = []
                }

                this._check_same_legend(type)
                this.transmit_options_status();
            },

            _check_same_legend(type) {
                const checked_in_type_list = this.legend_options_checked.filter( id => this.legend_type_list[type].includes(id))
                if (!this.legend_type_list[type].length) return
                if (this.legend_lock[type]) {
                    this.legend_options_checked = this.legend_options_checked.filter( id => !this.legend_type_list[type].includes(id))
                } else {
                    const other_id = this.legend_type_list[type].filter( id => !checked_in_type_list.includes(id))
                    this.legend_options_checked = [...this.legend_options_checked, ...other_id]
                    if (this.input_limit === 1) {
                        this.legend_options_checked = this.legend_options_checked.filter( id => this.legend_type_list[type].includes(id))
                        this._set_legend_lock(type)
                    }
                }
                
                this.legend_lock[type] = !this.legend_lock[type]
            },

            _set_legend_lock(type) {
                Object.keys(this.legend_lock).forEach( key => {
                    if (type === key) return
                    this.legend_lock[key] = false
                })
            },

            check_color_bar_option() {
                if (this._is_options_exceed_limit(this.colorbar_options_checked)) {
                    this.colorbar_options_checked = this._unshift_first_option_checked(this.colorbar_options_checked)
                }
                if (this.input_limit === 1) {
                    this.legend_options_checked = []
                    this._set_legend_lock()
                }
                this.transmit_options_status();
            },

            _is_options_exceed_limit(options_checked) {
                return options_checked.length > this.input_limit
            },

            _unshift_first_option_checked(options_checked) {
                const [ , ...keep_options ] = options_checked
                return keep_options
            },

            transmit_options_status () {
                // 圖標全部隱藏後，menu開關動作會使圖標顯示出來，因此在每次變動
                if (this.is_hide_all) {
                    this.$emit("set_maptool_list", 'colorbars', []);
                    this.$emit("set_maptool_list", 'legends', []);
                } else {
                    this.$emit("set_maptool_list", 'colorbars', this.colorbar_options_checked);
                    this.$emit("set_maptool_list", 'legends', this.legend_options_checked);
                }
            }
        },
    }
</script>

<style lang="scss" scoped>
    @import '../../../css/mixin';

    .map-tool {
        position: absolute;
        bottom: 5.5em;
        left: .5em;
        color: #fff;
        z-index: 3;
        touch-action: none;

        @include mobile {
            bottom: 7rem;
        }

        &__close-btn {
            position: absolute;
            top: 7px;
            right: 8px;
        }

        &__btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 64px;
            height: 32px;
            background: var(--base-dark-blue);
            border: 1px solid #fff;
            border-radius: 16px;
            box-sizing: border-box;
            cursor: pointer;
        }

        &__setbar {
            position: absolute;
            bottom: 0;
            left: calc(100% + 15px);
            overflow: hidden;
            min-width: 200px;
            background: var(--base-gray);
            border-radius: 16px;

            @include mobile {
                bottom: 2rem;
            }
        }

        &__title {
            display: flex;
            align-items: center;
            padding: 10px 24px;
            font-size: 1em;
            background: var(--linear-gradient-color-1);

            i::before {
                margin: 0 4px 0 0;
            }
        }

        &__content {
            label {
                position: relative;
                display: flex;
                align-items: center;
                margin: 0 12px;
                padding: 2px 12px;
                font-size: .875em;
                font-weight: normal;
                letter-spacing: .6px;
                white-space: nowrap;
                border-left: 1px solid var(--base-gray-darken);
                cursor: pointer;

                span {
                    display: inline-flex;

                    @include mobile {
                        white-space: break-spaces;
                    }
                }

                input {
                    display: none;
                }

                div {
                    display: inline-block;
                    margin-right: .25rem;
                    &::after {
                        display: block;
                        font-size: 24px;
                        font-family: 'fontello';
                        visibility: visible;
                    }

                    &.checkbox::after {
                        content: '\e81f';
                    }
                    &.checkbox.checked::after {
                        content: '\e81e';
                    }
                    &.radio::after {
                        content: '\e849';
                    }
                    &.radio.checked::after {
                        content: '\e848';
                    }
                }
            }
        }

        &__hide-all {
            --base-color: #fff;
            --hide-color: rgba(255, 255, 255, .6);

            display: flex;
            padding: 8px 20px;
            font-size: .875em;
            color: var(--hide-color);
            border-top: 8px solid #121624;
            cursor: pointer;

            i {
                color: var(--hide-color);
            }

            &.disable {
                color: var(--base-color);

                i {
                    color: var(--base-color);
                }
            }
        }
    }
</style>